import { QuestionOutlined, SyncOutlined } from '@ant-design/icons';
import { EnumLoginDetailsProvenienzaLogin } from '@framework/api-client/api/users/schemas/enumLoginDetailsProvenienzaLogin.ts';
import { type LoginDetailsProvenienzaLogin } from '@framework/api-client/api/users/schemas/loginDetailsProvenienzaLogin.ts';
import { getNetworkErrorMessage } from '@framework/auth/http/errors/getNetworkErrorMessage.ts';
import { assertNever } from '@framework/engine/utils/assertNever.ts';
import { ISODate } from '@framework/ui/components/date/ISODate.tsx';
import { Alert, Avatar, Flex, List, Space } from 'antd';
import styled from 'styled-components';
import { type useLastLoginHistory } from './useLastLoginHistory.ts';

export interface ILastLoginDrawerContentProps {
  historyData: ReturnType<typeof useLastLoginHistory>;
}

const LoginTypeAvatar = styled(Avatar)`
  &.is-admin {
    background-color: ${props => props.theme.colorWarningActive}; // #f56a00;
  }
  &.is-fo {
    background-color: ${props => props.theme.colorSuccessActive}; //#2bd176;
  }
  &.is-bo {
    background-color: ${props => props.theme.colorInfoActive}; // #7265e6;
  }
`;

function getAvatarType(type?: LoginDetailsProvenienzaLogin) {
  if (!type) {
    return {
      children: <QuestionOutlined />,
    };
  }
  switch (type) {
    case EnumLoginDetailsProvenienzaLogin.CONFIGURATORE:
      return {
        className: 'is-admin',
        children: 'CO',
        title: 'Accesso Configuratore',
      };
    case EnumLoginDetailsProvenienzaLogin.BACKOFFICE:
      return {
        className: 'is-bo',
        children: 'BO',
        title: 'Accesso Backoffice',
      };
    case EnumLoginDetailsProvenienzaLogin.FRONTOFFICE:
      return {
        className: 'is-fo',
        children: 'FO',
        title: 'Accesso Frontoffice',
      };
  }
  assertNever(type);
}

/**
 * Content del Drawer con gli ultimi  accessi dell'utente
 */
export function LastLoginDrawerContent(props: ILastLoginDrawerContentProps) {
  const { history, isLoading, error } = props.historyData;

  if (isLoading) {
    return (
      <Space>
        <SyncOutlined spin />
        Caricamento dati...
      </Space>
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        message={getNetworkErrorMessage(
          error,
          'Errore durante il caricamento dei dati',
        )}
      />
    );
  }

  if (history.length === 0) {
    return <div>Nessun accesso registrato</div>;
  }
  return (
    <div>
      <List
        itemLayout="horizontal"
        dataSource={history}
        renderItem={(login, index) => (
          <List.Item>
            <List.Item.Meta
              avatar={
                <LoginTypeAvatar
                  size="small"
                  {...getAvatarType(login.provenienzaLogin)}
                />
              }
              title={
                <Flex vertical>
                  <Flex justify="space-between">
                    <Space>
                      <ISODate date={login.dataAccesso} format="DD/MM/YYYY" />
                      <b>
                        <ISODate date={login.dataAccesso} format="HH:mm:ss" />
                      </b>
                    </Space>
                    <div>
                      IP Address: <b>{login.ipAddress}</b>
                    </div>
                  </Flex>
                  {login.parsed && (
                    <Space>
                      <div>
                        Browser: <b>{login.parsed?.browser.name}</b>{' '}
                        {login.parsed?.browser.version}
                      </div>
                      <div>
                        {/* <ArrowRightOutlined /> */}
                        su
                      </div>
                      <div>
                        <b>{login.parsed?.os.name}</b>{' '}
                        {login.parsed?.os.version}
                      </div>
                    </Space>
                  )}
                </Flex>
              }
            />
          </List.Item>
        )}
      />
    </div>
  );
}
